import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { PhoneIcon, CheckIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';

const TollFreeSearchModal = ({ isOpen, onClose, onSelectNumber, userId }) => {
  
  const [tollFreeNumbers, setTollFreeNumbers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedNumber, setSelectedNumber] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [selectedPrefix, setSelectedPrefix] = useState('');

  const tollFreePrefixes = [
    { code: '833', name: '833' },
    { code: '844', name: '844' },
    { code: '855', name: '855' },
    { code: '866', name: '866' },
    { code: '877', name: '877' },
    { code: '888', name: '888' }
  ];

  // Reset state when modal opens/closes
  useEffect(() => {
    if (!isOpen) {
      setTollFreeNumbers([]);
      setSelectedNumber(null);
      setShowConfirmation(false);
      setIsSearching(false);
      setIsPurchasing(false);
      setSelectedPrefix('');
    } else {
      // Auto-search when modal opens
      handleSearch();
    }
  }, [isOpen]);

  const handleSearch = async (prefix = '') => {
    setIsSearching(true);
    try {
      const response = await fetch('/api/searchTollFreeNumbers', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId,
          selectedPrefix: prefix || undefined
        })
      });

      if (!response.ok) {
        throw new Error('Search failed');
      }

      const data = await response.json();
      setTollFreeNumbers(data.numbers || []);
      
      if (data.numbers.length === 0) {
        console.log('No toll-free numbers found');
      }
    } catch (error) {
      console.error('Toll-free search error:', error);
      setTollFreeNumbers([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleNumberSelect = (number) => {
    setSelectedNumber(number);
    setShowConfirmation(true);
  };

  const handleConfirmPurchase = async () => {
    if (!selectedNumber) return;

    setIsPurchasing(true);
    try {
      const response = await fetch('/api/purchasePhoneNumber', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          phoneNumber: selectedNumber.phone_number,
          userId: userId
        })
      });

      if (!response.ok) {
        throw new Error('Purchase failed');
      }

      const result = await response.json();
      
      if (result.success) {
        onSelectNumber({
          success: true,
          phoneNumber: selectedNumber.phone_number,
          telnyxId: result.telnyxId
        });
      } else {
        throw new Error(result.error || 'Purchase failed');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to purchase number. Please try again.');
    } finally {
      setIsPurchasing(false);
    }
  };

  const handleBack = () => {
    setShowConfirmation(false);
    setSelectedNumber(null);
  };

  const handlePrefixFilter = (prefix) => {
    setSelectedPrefix(prefix === selectedPrefix ? '' : prefix);
    handleSearch(prefix === selectedPrefix ? '' : prefix);
  };

  const handleClose = () => {
    if (!isPurchasing) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
      <DialogBackdrop className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
      
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="mx-auto w-full max-w-2xl bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl shadow-2xl">
          <div className="p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-green-500/20 backdrop-blur border border-green-400/30 flex items-center justify-center">
                  <PhoneIcon className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-white">
                    Toll-Free Numbers
                  </h3>
                  <p className="text-sm text-white/60 mt-1">
                    Choose from available toll-free numbers
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                disabled={isPurchasing}
                className="w-10 h-10 rounded-xl bg-white/10 backdrop-blur border border-white/20 flex items-center justify-center hover:bg-white/20 transition-all duration-200 disabled:opacity-50"
              >
                <XMarkIcon className="w-5 h-5 text-white/80" />
              </button>
            </div>

            {showConfirmation ? (
              /* Confirmation View */
              <div className="space-y-6">
                <div className="text-center">
                  <div className="text-white/60 text-sm mb-2">Selected Number:</div>
                  <div className="text-2xl font-mono text-white mb-4">
                    {selectedNumber?.formatted_number}
                  </div>
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-green-500/20 rounded-full border border-green-400/30">
                    <div className="w-2 h-2 bg-green-400 rounded-full"></div>
                    <span className="text-sm text-green-400">Toll-Free ({selectedNumber?.prefix})</span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={handleBack}
                    disabled={isPurchasing}
                    className="flex-1 py-3 px-4 rounded-xl border border-white/20 text-white/80 hover:bg-white/10 transition-all duration-200 disabled:opacity-50"
                  >
                    Back
                  </button>
                  <button
                    onClick={handleConfirmPurchase}
                    disabled={isPurchasing}
                    className="flex-1 py-3 px-4 rounded-xl bg-green-500/20 border border-green-400/30 text-green-400 hover:bg-green-500/30 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {isPurchasing ? (
                      <>
                        <div className="w-4 h-4 border-2 border-green-400/30 border-t-green-400 rounded-full animate-spin"></div>
                        Getting number...
                      </>
                    ) : (
                      <>
                        <CheckIcon className="w-4 h-4" />
                        Get This Number
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              /* Search Results View */
              <div className="space-y-4">
                {/* Prefix Filter */}
                <div className="space-y-2">
                  <div className="text-sm text-white/60">Filter by prefix:</div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handlePrefixFilter('')}
                      className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
                        selectedPrefix === '' 
                          ? 'bg-indigo-500/30 border border-indigo-400/50 text-indigo-300'
                          : 'bg-white/10 border border-white/20 text-white/60 hover:bg-white/20'
                      }`}
                    >
                      All
                    </button>
                    {tollFreePrefixes.map((prefix) => (
                      <button
                        key={prefix.code}
                        onClick={() => handlePrefixFilter(prefix.code)}
                        className={`px-3 py-1 rounded-lg text-sm transition-all duration-200 ${
                          selectedPrefix === prefix.code 
                            ? 'bg-indigo-500/30 border border-indigo-400/50 text-indigo-300'
                            : 'bg-white/10 border border-white/20 text-white/60 hover:bg-white/20'
                        }`}
                      >
                        {prefix.name}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Search Results */}
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {isSearching ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span className="ml-3 text-white/60">Searching for toll-free numbers...</span>
                    </div>
                  ) : tollFreeNumbers.length === 0 ? (
                    <div className="text-center py-8">
                      <PhoneIcon className="w-12 h-12 text-white/30 mx-auto mb-3" />
                      <p className="text-white/60">No toll-free numbers available</p>
                      <p className="text-sm text-white/40 mt-1">Try a different prefix or check back later</p>
                    </div>
                  ) : (
                    tollFreeNumbers.map((number, index) => (
                      <button
                        key={`${number.phone_number}-${index}`}
                        onClick={() => handleNumberSelect(number)}
                        className="w-full p-4 rounded-xl border border-white/20 hover:border-white/30 hover:bg-white/10 transition-all duration-200 flex items-center justify-between group"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-green-500/20 backdrop-blur border border-green-400/30 flex items-center justify-center">
                            <PhoneIcon className="w-6 h-6 text-green-400" />
                          </div>
                          <div className="text-left">
                            <div className="text-white font-mono text-lg">
                              {number.formatted_number}
                            </div>
                            <div className="flex items-center gap-2 mt-1">
                              <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-500/20 rounded-full border border-green-400/30">
                                <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                                <span className="text-xs text-green-400">Toll-Free ({number.prefix})</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        <ChevronRightIcon className="w-5 h-5 text-white/40 group-hover:text-white/60 transition-colors" />
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
};

TollFreeSearchModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectNumber: PropTypes.func.isRequired,
  userId: PropTypes.string.isRequired
};

export default TollFreeSearchModal;
